import * as React from 'react';
import { observer } from 'mobx-react';
import { Row } from 'elc-grid';
import { gridItemType } from '../../constants/ProductGrid';
import { GridItem } from '../../domain/entities/GridItem';
import { ColumnRenderer } from '../common/ColumnRenderer';
import { CollectionItem, CollectionItemType } from '../product-grid/CollectionItem';
import {
    ISizePickerImages,
    IShortDescriptionConfig,
    IProductBenefitTypes
} from '../../interfaces/IProdcat';
import { TOGGLE_TYPE } from '../../constants/AutoReplenish';
import { classes } from '../../utils/Classes';
import {
    ITranslationsOverwrites,
    IProductImages
} from '../../../exported/product-grid-wrapper/ProductGridWrapper';
import { PRODUCT_BRIEF_CLASS_NAMES } from '../../constants/ProductBriefCollection';
import styled from 'styled-components';
import { Breakpoints } from 'elc-base-theme';

export interface IProductBriefWrapper {
    breakpoints: IBreakpointsWidth;
}

export interface IBreakpointsWidth {
    columnWithMobile: string;
    columnWithDesktop: string;
    columnWithLargeScreen: string;
    columnWithExtraLargeScreen: string;
}

export interface IBreakpoints {
    numColumnsMobile: number;
    numColumnsDesktop: number;
    numColumnsLargeScreen: number;
    numColumnsExtraLargeScreen: number;
}

const ITEMS_CLASSNAME = 'js-grid-styled-column';

export interface IProductBriefCollection {
    config: {
        gridItems: GridItem[];
        breakpoints: IBreakpoints;
        defaultBreakpoints: IBreakpoints;
        hideProductBadge?: boolean;
        showSubDisplayName?: boolean;
        hideProductPrice?: boolean;
        hideProductPriceRange?: boolean;
        hideProductRating?: boolean;
        ctaType?: string;
        enableNotifyMe?: boolean;
        enableQuickView?: boolean;
        enableMobileQuickView?: boolean;
        toutWidth?: number;
        toutHeight?: number;
        hideReviewsNumber?: boolean;
        productGridHideReadReviewsLink?: boolean;
        enableGridCarousel?: boolean;
        featuredProductId?: string;
        enableHoverInteraction?: boolean;
        enableShadePicker?: boolean;
        enableSizePicker?: boolean;
        shortDescriptionLocation?: IShortDescriptionConfig;
        enableProductDetailLink?: boolean;
        enableQuickViewShortcut?: boolean;
        featuredProductOverrideImage?: string;
        enableLearnMoreLink?: boolean;
        isLoaded: boolean;
        autoReplenishOptions: number[];
        autoReplenishToggleType?: TOGGLE_TYPE;
        enableNumberShades?: boolean;
        sizePickerImages?: ISizePickerImages;
        hideProductPricePerUnit?: boolean;
        showProductQuantitySelector?: boolean;
        hideEmptyStars?: boolean;
        enableAutoReplenishment?: boolean;
        translationsOverwrites?: ITranslationsOverwrites;
        positionGridInsideAccordion?: boolean;
        productImages?: IProductImages;
        lazyLoadGridItem?: boolean;
        productBenefitTypes?: IProductBenefitTypes;
    };
    className?: string;
    isMobile: boolean;
    areFiltersApplied?: boolean;
    isAllGiftsFilterApplied?: boolean;
}
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IProductBriefCollectionWrapper extends Omit<IProductBriefCollection, 'isMobile'> {}

const ProductBriefWrapper = styled.div<IProductBriefWrapper>`
    display: grid;
    grid-auto-flow: dense;
    grid-gap: 0;
    grid-template-columns: repeat(
        auto-fill,
        minmax(${({ breakpoints }) => breakpoints.columnWithMobile}, 1fr)
    );
    @media (min-width: ${Breakpoints.desktop}px) {
        grid-template-columns: repeat(
            auto-fill,
            minmax(${({ breakpoints }) => breakpoints.columnWithDesktop}, 1fr)
        );
    }
    @media (min-width: ${Breakpoints.largeScreen}px) {
        grid-template-columns: repeat(
            auto-fill,
            minmax(${({ breakpoints }) => breakpoints.columnWithLargeScreen}, 1fr)
        );
    }
    @media (min-width: ${Breakpoints.LargeScreen}px) {
        grid-template-columns: repeat(
            auto-fill,
            minmax(${({ breakpoints }) => breakpoints.columnWithExtraLargeScreen}, 1fr)
        );
    }
`;

@observer
export class ProductBriefCollection extends React.Component<IProductBriefCollection> {
    private getOverrideImage(item: GridItem, imageOverride?: string) {
        if (item.itemType !== gridItemType.featuredProduct || !imageOverride) {
            return '';
        }

        return imageOverride;
    }

    private getPercentByColumnNumber(columns: number): string {
        return `${100 / columns}%`;
    }

    private getColumnsWith = (): IBreakpointsWidth => {
        const { breakpoints } = this.props.config;
        const {
            numColumnsMobile,
            numColumnsDesktop,
            numColumnsLargeScreen,
            numColumnsExtraLargeScreen
        } = breakpoints;

        return {
            columnWithMobile: this.getPercentByColumnNumber(numColumnsMobile),
            columnWithDesktop: this.getPercentByColumnNumber(numColumnsDesktop),
            columnWithLargeScreen: this.getPercentByColumnNumber(numColumnsLargeScreen),
            columnWithExtraLargeScreen: this.getPercentByColumnNumber(numColumnsExtraLargeScreen)
        };
    };

    private isFeatured(gridItem: GridItem) {
        return !this.props.areFiltersApplied
            ? gridItem.itemType === gridItemType.featuredProduct
            : false;
    }

    private getBriefProps(gridItem: GridItem) {
        const {
            hideProductBadge,
            showSubDisplayName,
            hideProductPrice,
            hideProductPriceRange,
            hideProductRating,
            ctaType,
            enableNotifyMe,
            enableQuickView,
            enableMobileQuickView,
            productGridHideReadReviewsLink,
            hideReviewsNumber,
            enableHoverInteraction,
            enableShadePicker,
            enableSizePicker,
            shortDescriptionLocation,
            enableProductDetailLink,
            enableQuickViewShortcut,
            featuredProductOverrideImage,
            enableLearnMoreLink,
            autoReplenishOptions,
            autoReplenishToggleType,
            enableNumberShades,
            sizePickerImages,
            hideProductPricePerUnit,
            showProductQuantitySelector,
            hideEmptyStars,
            enableAutoReplenishment,
            translationsOverwrites,
            productImages,
            lazyLoadGridItem,
            isLoaded,
            productBenefitTypes
        } = this.props.config;

        return {
            key: `column-for-${gridItem.itemId}`,
            className: ITEMS_CLASSNAME,
            positionIndex: gridItem.positionIndex,
            hideProductBadge,
            showSubDisplayName,
            hideProductPrice,
            hideProductPriceRange,
            hideProductRating,
            ctaType,
            enableNotifyMe,
            enableQuickView,
            enableMobileQuickView,
            productGridHideReadReviewsLink,
            hideReviewsNumber,
            enableHoverInteraction,
            enableShadePicker,
            enableSizePicker,
            shortDescriptionLocation,
            enableProductDetailLink,
            enableQuickViewShortcut,
            imageOverride: this.getOverrideImage(gridItem, featuredProductOverrideImage),
            isFeatured: this.isFeatured(gridItem),
            featuredProductOverrideImage,
            enableLearnMoreLink,
            autoReplenishOptions,
            autoReplenishToggleType,
            enableNumberShades,
            sizePickerImages,
            hideProductPricePerUnit,
            showProductQuantitySelector,
            hideEmptyStars,
            enableAutoReplenishment,
            translationsOverwrites,
            isLoaded,
            productImages,
            lazyLoadGridItem,
            isMobile: this.props.isMobile,
            productBenefitTypes
        };
    }

    private getItemToRender(item: GridItem) {
        switch (item.itemType) {
            case gridItemType.product:
            case gridItemType.featuredProduct:
                return (
                    item.product && (
                        <CollectionItem
                            collectionItem={CollectionItemType.PRODUCT}
                            id={item.itemId}
                            productBriefProps={{
                                ...this.getBriefProps(item),
                                product: item.product
                            }}
                        />
                    )
                );
            default:
                return <CollectionItem collectionItem={CollectionItemType.TOUT} id={item.itemId} />;
        }
    }

    private renderBriefViewContent = () => {
        const {
            config: { gridItems = [], enableGridCarousel },
            isMobile
        } = this.props;

        return gridItems.reduce((components: React.ReactElement[], item) => {
            components.push(
                <ColumnRenderer
                    key={`column-for-${item.itemId}`}
                    isCarousel={enableGridCarousel}
                    isMobile={isMobile}
                    columns={item.w}
                    rows={item.h}
                    className={classes(
                        `grid-item-${item.itemType}`,
                        PRODUCT_BRIEF_CLASS_NAMES.GRID_ITEM_CLASS_NAME
                    )}
                >
                    {this.getItemToRender(item)}
                </ColumnRenderer>
            );

            return components;
        }, []);
    };

    private renderBriefView = () => {
        const {
            config: { isLoaded, enableGridCarousel }
        } = this.props;

        if (enableGridCarousel || !isLoaded) {
            return <Row>{this.renderBriefViewContent()}</Row>;
        }

        return this.renderBriefViewContent();
    };

    public render() {
        const productBriefViewClassNames = classes('product-brief-view', this.props.className);

        return (
            <ProductBriefWrapper
                breakpoints={this.getColumnsWith()}
                className={productBriefViewClassNames}
            >
                {this.renderBriefView()}
            </ProductBriefWrapper>
        );
    }
}
